<div [class]="layout.shell">
  <header [class]="layout.header">
    <div class="flex flex-col gap-0.5">
      <div class="flex items-center gap-2">
        <mat-icon class="h-5! w-5! shrink-0 text-xl! leading-5! text-primary-600">pending_actions</mat-icon>
        <h2 class="min-w-0 flex-1 truncate text-lg! leading-5! font-semibold! text-neutral-800">{{ "guaranteeV2.action.lineExecution" | translate }}</h2>
      </div>
      <p class="pl-7 text-sm text-neutral-500">{{ stepHintKey[step()] | translate }}</p>
    </div>

    <!-- What is being arranged, kept in view: every answer below is about this one service. -->
    <div class="mt-1.5 flex items-center gap-2 rounded-lg bg-neutral-50 px-2 py-1.5">
      <mat-icon
        class="h-4! w-4! shrink-0 text-base! leading-4! text-neutral-400"
        [matTooltip]="kindLabelKey[prescription.kind] | translate"
        >{{ kindIcon[prescription.kind] }}</mat-icon
      >
      <span class="min-w-0 flex-1 truncate text-sm font-semibold text-neutral-700">{{ prescription.name }}</span>
      @if (prescription.code) {
        <span
          class="max-w-1/2 min-w-0 truncate rounded-md bg-neutral-100 px-1.5 py-0.5 font-mono text-sm text-neutral-600"
          [matTooltip]="prescription.code"
          >{{ prescription.code }}</span
        >
      }
    </div>
  </header>

  <!-- A route, not a progress bar: any answered step can be returned to. -->
  <guarantees-step-nav
    class="shrink-0 px-4 pb-3"
    [steps]="stepEntries()"
    [current]="step()"
    (selected)="goTo($any($event))"
  ></guarantees-step-nav>

  <div [class]="layout.body">
    @switch (step()) {
      @case ("source") {
        <section class="flex flex-col gap-2">
          <!-- The three cases a manager arrives with, offered whether or not each is built. -->
          @for (option of sources; track option.id) {
            <button
              guaranteesToggle
              shape="card"
              layout="block"
              size="lg"
              align="start"
              type="button"
              [pressed]="source() === option.id"
              (click)="setSource(option.id)"
            >
              <mat-icon
                class="mt-0.5 h-4! w-4! shrink-0 text-base! leading-4!"
                [class]="source() === option.id ? 'text-primary-600' : 'text-neutral-400'"
                >{{ option.icon }}</mat-icon
              >

              <span class="flex min-w-0 flex-1 flex-col gap-0.5">
                <span class="text-sm font-semibold text-neutral-700">{{ option.labelKey | translate }}</span>
                <span class="text-sm text-neutral-500">{{ option.hintKey | translate }}</span>
              </span>
            </button>
          }

          @if (blockerKey) {
            <guarantees-callout
              tone="error"
              icon="block"
            >
              {{ blockerKey | translate }}
            </guarantees-callout>
          }
        </section>
      }

      @case ("order") {
        <section class="flex flex-col gap-3">
          @if (blockerKey) {
            <!-- The reason stands alone, and the step offers nothing to answer with while it does. -->
            <guarantees-callout
              tone="error"
              icon="block"
            >
              {{ blockerKey | translate }}
            </guarantees-callout>
          } @else {
            <!-- The other lines first: the partner is asked for an hour or a shelf that fits the whole
                 set, so the set is settled before the place is searched. The opened line is named in
                 the header and always covered, so only the choice is listed — and only where there is
                 one to make: one order covering a course is the reason this exists. -->
            @if (data.siblings.length > 0) {
              <fieldset class="flex min-w-0 flex-col gap-1">
                <legend
                  guaranteesLabel
                  class="pb-1"
                >
                  {{ "guaranteeV2.label.addToOrder" | translate }}
                </legend>

                @for (row of siblingRows; track row.line.prescription.id) {
                  <button
                    guaranteesToggle
                    shape="card"
                    layout="block"
                    size="md"
                    type="button"
                    [disabled]="!!row.blockerKey"
                    [pressed]="isCovered(row.line.prescription.id)"
                    (click)="toggleCovered(row)"
                  >
                    <guarantees-check-box [checked]="isCovered(row.line.prescription.id)"></guarantees-check-box>

                    <span class="flex min-w-0 flex-1 flex-col">
                      <span class="min-w-0 truncate text-sm text-neutral-700">{{ row.line.prescription.name }}</span>

                      <!-- Refused rather than hidden: a line missing from the list looks like an oversight. -->
                      @if (row.blockerKey) {
                        <span class="min-w-0 truncate text-sm text-neutral-400">{{ row.blockerKey | translate }}</span>
                      }
                    </span>

                    @if (row.line.prescription.code) {
                      <span
                        class="max-w-1/2 min-w-0 truncate rounded-md bg-neutral-100 px-1.5 py-0.5 font-mono text-sm text-neutral-500"
                        [matTooltip]="row.line.prescription.code"
                        >{{ row.line.prescription.code }}</span
                      >
                    }
                  </button>
                }
              </fieldset>
            }

            @if (restriction) {
              <!-- Said where the list is short, so a short list is not read as a partner with nothing free. -->
              <guarantees-callout
                tone="primary"
                icon="place"
              >
                {{ (restriction.doctorId ? "guaranteeV2.message.restrictedToDoctor" : "guaranteeV2.message.restrictedToPartner") | translate }}
              </guarantees-callout>
            }

            @if (binding()) {
              <!-- One row answers the whole step: the record states where, who and when all at once. -->
              <guarantees-candidate-picker
                [memberId]="data.memberId"
                [kind]="candidateKind"
                [selected]="candidate()"
                (chosen)="chooseCandidate($event)"
              ></guarantees-candidate-picker>
            } @else if (booking()) {
              <!-- The partner's own offer, taken as it stands: an hour it published, or a shelf it holds. -->
              @if (ordersDrugs) {
                <guarantees-basket-picker
                  [memberId]="data.memberId"
                  [prescriptionIds]="coveredIds()"
                  [initial]="basket()"
                  [initialArea]="area()"
                  (areaChanged)="area.set($event)"
                  (changed)="basket.set($event)"
                ></guarantees-basket-picker>
              } @else if (!kindResolved()) {
                <!-- Nothing is asked of a partner until it is known which question this line is. -->
                <div class="flex items-center gap-2 px-2 py-3 text-sm text-neutral-400">
                  <guarantees-spinner></guarantees-spinner>
                  {{ "guaranteeV2.message.resolvingService" | translate }}
                </div>
              } @else if (needsDoctorStep()) {
                <!-- The person first, the hour second: the partner's schedule will not answer without a
                     doctor named, and the member's own app asks in the same order. -->
                <guarantees-doctor-picker
                  [userId]="prescription.userId"
                  [selected]="doctor()"
                  (chosen)="chooseDoctor($event)"
                ></guarantees-doctor-picker>
              } @else {
                @if (doctor(); as chosenDoctor) {
                  <div class="flex items-center gap-2 rounded-lg border border-primary-200 bg-primary-50/50 px-2.5 py-1.5">
                    <mat-icon class="h-4! w-4! shrink-0 text-base! leading-4! text-primary-600">person</mat-icon>

                    <span class="flex min-w-0 flex-1 flex-col">
                      <span class="min-w-0 truncate text-sm font-semibold text-neutral-700">{{ chosenDoctor.fullName }}</span>

                      @if (chosenDoctor.specialization) {
                        <span class="min-w-0 truncate text-sm text-neutral-500">{{ chosenDoctor.specialization }}</span>
                      }
                    </span>

                    <button
                      class="shrink-0 rounded-full border border-neutral-200 bg-neutral-0 px-2 py-0.5 text-sm font-semibold text-neutral-600 hover:bg-neutral-50 focus-visible:ring-2 focus-visible:ring-primary-500/40 focus-visible:outline-none"
                      type="button"
                      (click)="clearDoctor()"
                    >
                      {{ "guaranteeV2.action.changeFamilyDoctor" | translate }}
                    </button>
                  </div>
                }

                <guarantees-slot-picker
                  [memberId]="data.memberId"
                  [kind]="slotKind"
                  [familyDoctor]="familyDoctor()"
                  [doctorId]="searchDoctorId()"
                  [partnerId]="searchPartnerId()"
                  [prescriptionIds]="coveredIds()"
                  [initial]="slot()"
                  [initialArea]="area()"
                  (areaChanged)="area.set($event)"
                  (changed)="slot.set($event)"
                ></guarantees-slot-picker>
              }
            } @else {
              <guarantees-place-picker
                [service]="partnerService"
                [withDoctor]="withDoctor"
                [initial]="place()"
                (changed)="place.set($event)"
              ></guarantees-place-picker>

              <!-- The order is the partner's, so its number is what our record points at it by. -->
              @if (offersOrderNumber()) {
                <label class="flex flex-col gap-1">
                  <span guaranteesLabel>{{ "guaranteeV2.label.orderNumber" | translate }}</span>
                  <input
                    guaranteesControl
                    class="w-full"
                    name="orderNumber"
                    type="text"
                    autocomplete="off"
                    [placeholder]="'guaranteeV2.message.orderNumberPlaceholder' | translate"
                    [ngModel]="orderNumber()"
                    (ngModelChange)="orderNumber.set($event)"
                  />
                  <span class="text-sm text-neutral-400">{{ "guaranteeV2.message.orderNumberRecordedOnly" | translate }}</span>
                </label>
              }

              <div class="flex flex-wrap items-end gap-2">
                <label class="flex min-w-32 flex-1 flex-col gap-1">
                  <span
                    guaranteesLabel
                    required
                    [missing]="!date()"
                    >{{ "guaranteeV2.label.plannedDate" | translate }}</span
                  >
                  <input
                    guaranteesControl
                    class="w-full"
                    name="plannedDate"
                    type="date"
                    [ngModel]="date()"
                    (ngModelChange)="date.set($event)"
                  />
                </label>

                <label class="flex w-24 flex-col gap-1">
                  <span
                    guaranteesLabel
                    required
                    [missing]="!startTime()"
                    >{{ "guaranteeV2.label.timeFrom" | translate }}</span
                  >
                  <input
                    guaranteesControl
                    class="w-full"
                    name="startTime"
                    type="time"
                    [ngModel]="startTime()"
                    (ngModelChange)="setStart($event)"
                  />
                </label>

                @if (withEndTime) {
                  <label class="flex w-24 flex-col gap-1">
                    <span guaranteesLabel>{{ "guaranteeV2.label.timeTo" | translate }}</span>
                    <input
                      guaranteesControl
                      class="w-full"
                      name="endTime"
                      type="time"
                      [ngModel]="endTime()"
                      (ngModelChange)="endTime.set($event)"
                    />
                  </label>
                }
              </div>
            }
          }
        </section>
      }

      @case ("terms") {
        <section class="flex flex-col gap-3">
          <!-- Priced line by line: one order delivers several services, and each has a price of its own. -->
          <fieldset class="flex min-w-0 flex-col gap-1">
            <legend
              guaranteesLabel
              class="pb-1"
            >
              {{ "guaranteeV2.label.linePrices" | translate }}
            </legend>

            @for (row of priceRows(); track row.id) {
              <label class="flex items-center gap-2 rounded-lg bg-neutral-50 px-2 py-1.5">
                <span class="flex min-w-0 flex-1 flex-col">
                  <span class="min-w-0 truncate text-sm text-neutral-700">{{ row.name }}</span>
                  @if (row.code) {
                    <span
                      class="min-w-0 truncate font-mono text-sm text-neutral-400"
                      [matTooltip]="row.code"
                      >{{ row.code }}</span
                    >
                  }
                </span>
                <input
                  guaranteesControl
                  class="w-28 shrink-0 tabular-nums"
                  type="number"
                  min="0"
                  step="0.01"
                  [name]="'price-' + row.id"
                  [ngModel]="row.price"
                  (ngModelChange)="setPrice(row.id, $event)"
                />
              </label>
            }
          </fieldset>

          <label class="flex w-28 flex-col gap-1">
            <span guaranteesLabel>{{ "guaranteeV2.label.coverageFranchise" | translate }}</span>
            <input
              guaranteesControl
              class="w-full tabular-nums"
              name="franchise"
              type="number"
              min="0"
              step="1"
              [ngModel]="franchise()"
              (ngModelChange)="franchise.set($event)"
            />
          </label>

          <!-- Offered only where no contract covered the care, which is the check it turns off. -->
          @if (offersConsiderPaid) {
            <button
              guaranteesToggle
              shape="card"
              layout="block"
              size="lg"
              type="button"
              [pressed]="considerPaid()"
              (click)="considerPaid.set(!considerPaid())"
            >
              <guarantees-check-box [checked]="considerPaid()"></guarantees-check-box>
              <span class="flex min-w-0 flex-col">
                <span class="text-sm font-semibold text-neutral-700">{{ "guaranteeV2.action.considerPaid" | translate }}</span>
                <span class="text-sm text-neutral-400">{{ "guaranteeV2.message.considerPaidHint" | translate }}</span>
              </span>
            </button>
          }

          <!-- The record as it will read, so the last click is made on the answer rather than on trust. -->
          <dl class="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 rounded-lg bg-neutral-50 px-2.5 py-2 text-sm">
            <dt class="text-neutral-400">{{ "guaranteeV2.label.partner" | translate }}</dt>
            <dd class="min-w-0 truncate font-semibold text-neutral-700">{{ summary().partnerName || "—" }}</dd>

            <dt class="text-neutral-400">{{ "guaranteeV2.label.location" | translate }}</dt>
            <dd class="min-w-0 truncate text-neutral-600">{{ summary().place || "—" }}</dd>

            @if (withDoctor) {
              <dt class="text-neutral-400">{{ "guaranteeV2.label.doctor" | translate }}</dt>
              <dd class="min-w-0 truncate text-neutral-600">{{ summary().doctorName || "—" }}</dd>
            }

            @if (summary().orderNumber; as orderNo) {
              <dt class="text-neutral-400">{{ "guaranteeV2.label.orderNumber" | translate }}</dt>
              <dd class="min-w-0 truncate font-mono text-neutral-600">{{ orderNo }}</dd>
            }

            <dt class="text-neutral-400">{{ "guaranteeV2.label.plannedDate" | translate }}</dt>
            <dd class="text-neutral-600 tabular-nums">
              {{ summary().startsAt ? (summary().startsAt | date: "d MMM yyyy HH:mm") : "—" }}
              @if (summary().endsAt; as endsAt) {
                <span>– {{ endsAt | date: "HH:mm" }}</span>
              }
            </dd>

            <dt class="text-neutral-400">{{ "guaranteeV2.label.coveredLines" | translate }}</dt>
            <dd class="text-neutral-600 tabular-nums">{{ "guaranteeV2.label.linesCount" | translate: { count: coveredIds().length } }}</dd>

            <dt class="text-neutral-400">{{ "guaranteeV2.label.totalPrice" | translate }}</dt>
            <dd class="font-semibold text-neutral-700 tabular-nums">
              @if (totalPrice() !== undefined) {
                {{ totalPrice() | number: "1.0-2" }}&nbsp;&#8372;
              } @else {
                —
              }
            </dd>
          </dl>

          <!-- Said once, where it matters: what saving does differs between the acts, and where it
               would do nothing at all that is what stands here instead. -->
          @if (blockerKey) {
            <guarantees-callout
              tone="error"
              icon="block"
            >
              {{ blockerKey | translate }}
            </guarantees-callout>
          } @else if (!canSubmit()) {
            <guarantees-callout
              tone="warning"
              icon="edit_note"
            >
              {{ submitBlockerKey() | translate }}
            </guarantees-callout>
          } @else if (binding()) {
            <guarantees-callout
              tone="primary"
              icon="link"
            >
              {{ "guaranteeV2.message.planningBindsRecord" | translate }}
            </guarantees-callout>
          } @else if (booking()) {
            <guarantees-callout
              tone="primary"
              icon="bolt"
            >
              {{ ordersDrugs ? ("guaranteeV2.message.planningPlacesOrder" | translate) : ("guaranteeV2.message.planningPlacesBooking" | translate) }}
            </guarantees-callout>
          } @else {
            <guarantees-callout
              tone="primary"
              icon="edit_calendar"
            >
              {{ "guaranteeV2.message.planningRecordsManual" | translate }}
            </guarantees-callout>
          }
        </section>
      }
    }
  </div>

  <footer [class]="layout.footer">
    <button
      class="inline-flex items-center rounded-full px-3 py-1.5 text-sm font-medium text-neutral-500 transition-colors duration-150 hover:bg-neutral-100 focus-visible:ring-2 focus-visible:ring-primary-500/40 focus-visible:outline-none motion-reduce:transition-none"
      type="button"
      (click)="cancel()"
    >
      {{ "guaranteeV2.action.cancel" | translate }}
    </button>

    <span class="ml-auto flex items-center gap-1.5">
      @if (!isFirst()) {
        <button
          class="inline-flex items-center gap-1 rounded-full border border-neutral-200 px-3 py-1.5 text-sm font-semibold text-neutral-600 hover:bg-neutral-50 focus-visible:ring-2 focus-visible:ring-primary-500/40 focus-visible:outline-none"
          type="button"
          (click)="back()"
        >
          <mat-icon class="h-3.5! w-3.5! text-sm! leading-3.5!">arrow_back</mat-icon>
          {{ "guaranteeV2.action.stepBack" | translate }}
        </button>
      }

      @if (!isLast()) {
        <button
          class="inline-flex items-center gap-1 rounded-full border border-neutral-200 px-3 py-1.5 text-sm font-semibold text-neutral-600 hover:bg-neutral-50 focus-visible:ring-2 focus-visible:ring-primary-500/40 focus-visible:outline-none"
          type="button"
          (click)="next()"
        >
          {{ "guaranteeV2.action.stepNext" | translate }}
          <mat-icon class="h-3.5! w-3.5! text-sm! leading-3.5!">arrow_forward</mat-icon>
        </button>
      }

      <!-- Reachable from every step: the terms all carry defaults, and the place is what is required. -->
      <button
        class="inline-flex items-center gap-1 rounded-full bg-primary-600 px-3 py-1.5 text-sm font-semibold text-neutral-0 transition-colors duration-150 hover:bg-primary-700 focus-visible:ring-2 focus-visible:ring-primary-500/40 focus-visible:outline-none disabled:opacity-40 motion-reduce:transition-none"
        type="button"
        [disabled]="!canSubmit()"
        (click)="submit()"
      >
        <mat-icon class="h-3.5! w-3.5! text-sm! leading-3.5!">event_available</mat-icon>
        {{ "guaranteeV2.action.savePlanning" | translate }}
      </button>
    </span>
  </footer>
</div>
